import type {Delimiter, Nodes} from 'mdast'
import type {Extension} from 'mdast-util-from-markdown'
import {visitParents} from 'unist-util-visit-parents'

import {isAdjacent, popChild, removeChild, unshiftChild} from '../../syntax-tree/tree-manipulation-utils'

/**
 * Nested blockquotes are parsed as a blockquote immediately preceded by the prefixes from the parent blockquote,
 * which results in a broken document structure for the user because the nested `<blockquote>` element would
 * start in-between two prefix tokens:
 * ```html
 * <blockquote>
 *   <span class="delimiter">&gt;</span> outer quote
 *   <span class="delimiter">&gt;</span>
 *   <blockquote>
 *     <span class="delimiter">&gt;</span> inner quote
 *   </blockquote>
 * </blockquote>
 * ```
 * To work around this, we modify nested blockquotes to 'consume' any immediately preceding prefixes by expanding the
 * start of the blockquote backward. This gives us a more usable structure when transformed to HTML:
 * ```html
 * <blockquote>
 *   <span class="delimiter">&gt;</span> outer quote
 *   <blockquote>
 *     <span class="delimiter">&gt;</span><span class="delimiter">&gt;</span> inner quote
 *   </blockquote>
 * </blockquote>
 * ```
 */
export function mdastExpandNestedBlockquotes(): Extension {
  return {
    transforms: [expandNestedBlockquotes],
  }
}

function isBlockquoteDelimiter(node?: Nodes): node is Delimiter {
  return node?.type === 'delimiter' && node.tokenType === 'blockQuotePrefix'
}

function expandNestedBlockquotes(tree: Nodes): void {
  visitParents(tree, 'blockquote', (node, ancestors) => {
    const parent = ancestors.at(-1)
    if (parent?.type !== 'blockquote') return

    for (
      let index = parent.children.indexOf(node) - 1, previousSibling = parent.children[index];
      previousSibling && isAdjacent(previousSibling, node);
      previousSibling = parent.children[--index]
    ) {
      if (isBlockquoteDelimiter(previousSibling)) {
        // Common case: Move preceding delimiter tokens from parent into child:
        //
        // blockquote         blockquote
        //   delimiter     →    blockquote
        //   blockquote           delimiter
        //     delimiter          delimiter

        // When removing the delimiter from the parent blockquote, shrink=false because we still want the parent to contain that position
        removeChild(parent, index, false)
        unshiftChild(node, previousSibling)
      } else {
        // Weird case: Lists that precede nested quotes end up with delimiters trailing inside the list node, so we have
        // to look _inside_ the preceding sibling for delimiters to consume.
        //
        // blockquote         blockquote
        //   list          →    list
        //     delimiter        blockquote
        //   blockquote           delimiter
        //     delimiter          delimiter
        for (
          let lastNephew = previousSibling.children?.at(-1);
          lastNephew && isAdjacent(lastNephew, node) && isBlockquoteDelimiter(lastNephew);
          lastNephew = previousSibling.children?.at(-1)
        ) {
          // In this case, we do want to shrink the parent so its end bounds don't overlap with the blockquote's start bounds
          popChild(previousSibling, true)
          unshiftChild(node, lastNephew)
        }
        break // We didn't remove this sibling, so we don't need to look at any more previous siblings
      }
    }

    // Note: we DON'T need to worry about recursing up the tree to look in ancestors' children in the case of deeply
    // nested blockquotes, because we will never encounter a tree like this:
    //
    // blockquote
    //   delimiter
    //   blockquote
    //     delimiter
    //     blockquote < this node
    //       delimiter
    //
    // This is because `visitParents` will travel from the top down in the tree, so we will have already transformed
    // the parent blockquote and thus will only ever see a tree like this:
    //
    // blockquote
    //   blockquote < already visited
    //     delimiter
    //     delimiter
    //     blockquote < this node
    //       delimiter
  })
}
